import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';

interface ICheckRegisterExcelDialogProps {
    dialogName: string;
    hideDialog: () => void;
    excelType: 'SUMMARY' | 'DETAIL';
}

// Mirrors the Excel block (shared by the main screen's "Excel Summary" button and the standalone
// "Excel Detail" button via :excel.type). Per the confirmed web-port rule, this skips the Oracle
// Form's file-save-location prompt entirely and downloads straight to the browser's default
// downloads folder.
//
// The "Type" field here is genuinely Contractor/Employee (CHECK_REGISTER.TYPE, matched via
// Oracle's instr(v_type, type) > 0 - a multi-char "allowed types" string), NOT paper-check-vs-
// direct-deposit - confirmed by reading Excel_DetailReportData's actual cursor. Oracle's own form
// item happens to be named :excel.check_type despite that, which is just a misleading name in the
// source, not evidence it's the CHECK_TYPE column.
const CheckRegisterExcelDialog = (props: ICheckRegisterExcelDialogProps) => {
    const { register, errors, getValues } = useForm();
    const { get, response } = useFetch();
    const [message, setMessage] = useState<string>('');

    const handleOk = async () => {
        setMessage('');
        const values = getValues();
        const params = new URLSearchParams({
            reportType: props.excelType,
            beginDate: values.beginCheckDate ?? '',
            endDate: values.endCheckDate ?? '',
            clientNumber: values.clientNumber ?? '',
            office: values.office ?? '',
            employeeType: values.employeeType ?? '',
        });

        await get(`/v1.0/Payroll/excel?${params}`);

        if (!response.ok) {
            const body = await response.text();
            setMessage(body || 'Unable to generate the Excel report. Please try again.');
            return;
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.getElementById('checkRegisterExcelDownload') as HTMLAnchorElement;
        a.href = blobUrl;
        a.download = props.excelType === 'DETAIL' ? 'CheckDetail.xlsx' : 'ContractorPay.xlsx';
        a.click();
        window.URL.revokeObjectURL(blobUrl);

        props.hideDialog();
    };

    return (
        <Dialog title={props.excelType === 'DETAIL' ? 'Check Detail - Excel' : 'Excel Summary'} dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '35%' }}>
            <a style={{ display: 'none' }} id="checkRegisterExcelDownload" />
            <div className="form-row">
                <RHFInputBox
                    label="Beg. Check Date"
                    name="beginCheckDate"
                    type="date"
                    register={register}
                    errors={errors}
                    labelOnLeft={true}
                    spaceLabelContent={true}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '50%' }}
                />
                <RHFInputBox
                    label="Client No."
                    name="clientNumber"
                    type="number"
                    register={register}
                    errors={errors}
                    labelOnLeft={true}
                    spaceLabelContent={true}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '50%' }}
                    title="Blank = All"
                />
            </div>
            <div className="form-row">
                <RHFInputBox
                    label="End Check Date"
                    name="endCheckDate"
                    type="date"
                    register={register}
                    errors={errors}
                    labelOnLeft={true}
                    spaceLabelContent={true}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '50%' }}
                />
                <RHFInputBox
                    label="Office"
                    name="office"
                    type="text"
                    register={register}
                    errors={errors}
                    labelOnLeft={true}
                    spaceLabelContent={true}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '50%' }}
                    title="Blank = All"
                />
            </div>
            <RHFSelect
                label="Type"
                name="employeeType"
                options={[{ value: 'C', label: 'Contractor' }, { value: 'E', label: 'Employee' }]}
                placeholder="Both"
                register={register}
                errors={errors}
                labelOnLeft={true}
                style={{ width: '100%' }}
                title="Blank = Both"
            />
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button className="blue-hover-button" style={{ width: '20%', marginRight: '1rem' }} text="OK" onClick={handleOk} />
                <Button className="blue-hover-button" style={{ width: '20%' }} text="Cancel" onClick={() => props.hideDialog()} />
            </div>
            <div style={{ marginTop: '1rem', minHeight: '1.2rem', textAlign: 'center', color: 'red' }}>{message}</div>
        </Dialog>
    );
};

export default CheckRegisterExcelDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/checkRegister/CheckRegisterExcelDialog.tsx